import React, { useEffect, useMemo, useState } from "react";
import { Link, useLocation, useSearchParams } from "react-router-dom";
import { AlertTriangle, MailCheck, RefreshCw, ShieldCheck } from "lucide-react";
import { useAuth } from "../contexts/AuthContext";
import { useTranslation } from "react-i18next";

const VerifyEmail = () => {
  const {
    verifyEmailToken,
    resendVerification,
    pendingVerificationEmail,
    isLoading,
  } = useAuth();
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const token = searchParams.get("token");
  const emailParam = searchParams.get("email");
  const derivedEmail =
    location.state?.email || emailParam || pendingVerificationEmail || "";

  const [email, setEmail] = useState(derivedEmail);
  const [status, setStatus] = useState(token ? "verifying" : "idle");
  const [message, setMessage] = useState("");
  const [resendLoading, setResendLoading] = useState(false);
  const [verificationLoading, setVerificationLoading] = useState(false);

  const statusCopy = useMemo(
    () => ({
      idle: {
        title: t("auth.verifyEmail.title"),
        description: t("auth.verifyEmail.subtitle"),
        Icon: ShieldCheck,
      },
      verifying: {
        title: t("auth.verifyEmail.verifying"),
        description: t("auth.verifyEmail.verifyingDesc"),
        Icon: RefreshCw,
      },
      success: {
        title: t("auth.verifyEmail.successTitle"),
        description: t("auth.verifyEmail.successDesc"),
        Icon: MailCheck,
      },
      error: {
        title: t("auth.verifyEmail.errorTitle"),
        description: t("auth.verifyEmail.errorDesc"),
        Icon: AlertTriangle,
      },
    }),
    [t],
  );

  useEffect(() => {
    setEmail(derivedEmail);
  }, [derivedEmail]);

  useEffect(() => {
    if (!token) return;

    let isMounted = true;
    const verify = async () => {
      setVerificationLoading(true);
      const result = await verifyEmailToken(token);
      if (!isMounted) return;

      if (result.success) {
        setStatus("success");
        setMessage(result.message || t("auth.verifyEmail.successDesc"));
      } else {
        setStatus("error");
        setMessage(result.error || t("auth.verifyEmail.errorDesc"));
      }

      setVerificationLoading(false);
    };

    verify();

    return () => {
      isMounted = false;
    };
  }, [token, verifyEmailToken]);

  const handleResend = async (event) => {
    event.preventDefault();

    if (!email) {
      setStatus("error");
      setMessage(t("auth.verifyEmail.emailRequired"));
      return;
    }

    setResendLoading(true);
    const result = await resendVerification(email);

    if (result.success) {
      setStatus("idle");
      setMessage(t("auth.verifyEmail.resendSuccess"));
    } else {
      setStatus("error");
      setMessage(result.error || t("auth.verifyEmail.resendError"));
    }

    setResendLoading(false);
  };

  const { title, description, Icon } = useMemo(() => {
    const state = statusCopy[status] || statusCopy.idle;
    return state;
  }, [status, statusCopy]);

  const showLoginCta = status === "success";
  const busy = resendLoading || verificationLoading || isLoading;

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950 text-white flex items-center justify-center px-4 py-12">
      <div className="w-full max-w-2xl bg-slate-900/70 border border-white/10 rounded-3xl shadow-2xl shadow-cyan-500/10 backdrop-blur-xl p-10 space-y-8">
        <header className="text-center space-y-4">
          <div className="w-16 h-16 rounded-2xl bg-cyan-500/20 border border-cyan-400/30 flex items-center justify-center mx-auto">
            <Icon className="h-8 w-8 text-cyan-300 animate-pulse" />
          </div>
          <div>
            <p className="uppercase tracking-[0.3em] text-xs text-cyan-200/70">
              {t("auth.verifyEmail.title")}
            </p>
            <h1 className="text-3xl font-semibold mt-2 text-white">{title}</h1>
            <p className="text-slate-300 mt-3 max-w-lg mx-auto">
              {description}
            </p>
          </div>
        </header>

        <div className="bg-slate-800/60 border border-white/5 rounded-2xl p-6 space-y-4">
          <p className="text-sm text-slate-200/80">{message}</p>
          {token && (
            <p className="text-xs text-slate-400/80">
              {t("auth.verifyEmail.tokenLabel")} {token.slice(0, 8)}…
              {token.slice(-6)}
            </p>
          )}
        </div>

        {status !== "success" && (
          <>
            <form onSubmit={handleResend} className="space-y-4">
              <label className="block text-sm font-medium text-slate-200">
                {t("auth.register.email")}
              </label>
              <div className="flex flex-col sm:flex-row gap-3">
                <input
                  type="email"
                  value={email}
                  onChange={(event) => setEmail(event.target.value)}
                  placeholder={t("auth.verifyEmail.emailPlaceholder")}
                  className="flex-1 rounded-2xl bg-slate-950/60 border border-white/10 px-4 py-3 text-white placeholder:text-slate-500 focus:outline-none focus:ring-2 focus:ring-cyan-400"
                />
                <button
                  type="submit"
                  disabled={busy}
                  className="inline-flex items-center justify-center rounded-2xl bg-gradient-to-r from-cyan-400 to-blue-500 px-6 py-3 font-semibold text-white shadow-lg shadow-cyan-500/25 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {resendLoading ? (
                    <RefreshCw className="h-5 w-5 animate-spin" />
                  ) : (
                    t("auth.verifyEmail.resend")
                  )}
                </button>
              </div>
            </form>

            <div className="flex flex-col gap-3 text-sm text-slate-300">
              <p>• {t("auth.verifyEmail.tip1")}</p>
              <p>• {t("auth.verifyEmail.tip2")}</p>
            </div>
          </>
        )}

        <div className="flex flex-wrap items-center gap-4 text-sm text-slate-400">
          <Link to="/terms" className="hover:text-cyan-300 transition">
            {t("legal.links.terms")}
          </Link>
          <span aria-hidden="true">•</span>
          <Link to="/privacy" className="hover:text-cyan-300 transition">
            {t("legal.links.privacy")}
          </Link>
          <span className="text-slate-500">
            {t("legal.versionLabel", { version: "2026-05-07" })}
          </span>
        </div>

        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <Link
            to="/login"
            className="text-cyan-300 hover:text-cyan-200 transition"
          >
            {t("auth.forgotPassword.backToLogin")}
          </Link>
          {showLoginCta && (
            <Link
              to="/login"
              className="inline-flex items-center justify-center rounded-2xl border border-cyan-300/60 px-6 py-3 text-sm font-semibold text-cyan-100 hover:bg-cyan-500/10"
            >
              {t("auth.verifyEmail.signIn")}
            </Link>
          )}
        </div>
      </div>
    </div>
  );
};

export default VerifyEmail;
